import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Baby, Plus, Pencil, Trash2, Loader2, AlertTriangle, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const EMPTY_DEPENDENT = { nome_completo: "", data_nascimento: "", sexo: "", telefone: "", herda_telefone: true, alergias: "", condicao_especial: "", observacoes_dependente: "" };

export const idadeDe = (dataNascimento) => {
  if (!dataNascimento) return null;
  const n = new Date(dataNascimento.slice(0, 10) + "T12:00:00");
  if (isNaN(n)) return null;
  const h = new Date();
  let i = h.getFullYear() - n.getFullYear();
  if (h.getMonth() < n.getMonth() || (h.getMonth() === n.getMonth() && h.getDate() < n.getDate())) i--;
  return i;
};

const errMsg = (err, fallback) => {
  const d = err.response?.data?.detail;
  return Array.isArray(d) ? d.map(x => x.msg).join("; ") : (typeof d === "string" ? d : fallback);
};

// Campos do dependente — reutilizado no Meu Perfil, Pessoas e cadastro público
export const DependentFields = ({ form, setForm, responsavelTelefone, idPrefix = "dependent" }) => {
  const idade = idadeDe(form.data_nascimento);
  const up = (patch) => setForm({ ...form, ...patch });
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label>Nome completo *</Label>
        <Input value={form.nome_completo || ""} onChange={(e) => up({ nome_completo: e.target.value })} data-testid={`${idPrefix}-nome`} />
      </div>
      <div className="grid grid-cols-2 gap-3 items-end">
        <div className="space-y-1.5">
          <Label className="whitespace-nowrap">Nascimento *</Label>
          <Input type="date" className="h-10 w-full min-w-0" value={form.data_nascimento || ""} onChange={(e) => up({ data_nascimento: e.target.value })} data-testid={`${idPrefix}-nascimento`} />
        </div>
        <div className="space-y-1.5">
          <Label>Sexo</Label>
          <Select value={form.sexo || ""} onValueChange={(v) => up({ sexo: v })}>
            <SelectTrigger className="h-10" data-testid={`${idPrefix}-sexo`}><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="masculino">Masculino</SelectItem>
              <SelectItem value="feminino">Feminino</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {idade !== null && (
        <p className="text-[11px] text-muted-foreground -mt-1" data-testid={`${idPrefix}-idade`}>
          {idade} anos {idade <= 11 ? "· Ministério Infantil" : "· fora da faixa infantil"}
        </p>
      )}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> Telefone</Label>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" className="h-3.5 w-3.5 accent-primary" checked={form.herda_telefone !== false} onChange={(e) => up({ herda_telefone: e.target.checked, telefone: e.target.checked ? "" : form.telefone })} data-testid={`${idPrefix}-herda-telefone`} />
            Usar o telefone do responsável
          </label>
        </div>
        {form.herda_telefone !== false ? (
          <div className="h-10 flex items-center px-3 rounded-md border bg-muted/40 text-sm text-muted-foreground" data-testid={`${idPrefix}-telefone-herdado`}>
            {responsavelTelefone || "Telefone do responsável"}
          </div>
        ) : (
          <Input type="tel" value={form.telefone || ""} onChange={(e) => up({ telefone: e.target.value })} placeholder="(00) 00000-0000" data-testid={`${idPrefix}-telefone`} />
        )}
      </div>
      <div className="space-y-1.5">
        <Label>Alergia a medicamentos ou alimentos</Label>
        <Input value={form.alergias || ""} onChange={(e) => up({ alergias: e.target.value })} placeholder="Ex.: dipirona, amendoim… ou 'nenhuma'" data-testid={`${idPrefix}-alergias`} />
      </div>
      <div className="space-y-1.5">
        <Label>Condição especial</Label>
        <Input value={form.condicao_especial || ""} onChange={(e) => up({ condicao_especial: e.target.value })} placeholder="Ex.: TEA, TDAH, asma…" data-testid={`${idPrefix}-condicao`} />
      </div>
      <div className="space-y-1.5">
        <Label>Observações</Label>
        <Textarea rows={2} value={form.observacoes_dependente || ""} onChange={(e) => up({ observacoes_dependente: e.target.value })} data-testid={`${idPrefix}-observacoes`} />
      </div>
    </div>
  );
};

export const DependentFormDialog = ({ open, onOpenChange, responsavelId, responsavelTelefone, dependente, onSaved }) => {
  const [form, setForm] = useState(EMPTY_DEPENDENT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(dependente ? { ...EMPTY_DEPENDENT, ...dependente, herda_telefone: dependente.herda_telefone !== false } : EMPTY_DEPENDENT);
  }, [open, dependente]);

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.nome_completo.trim() || !form.data_nascimento) return toast.error("Informe nome e data de nascimento");
    setSaving(true);
    try {
      const payload = { ...form, sexo: form.sexo || null };
      if (dependente?.id) await axios.put(`${API}/people/${responsavelId}/dependentes/${dependente.id}`, payload);
      else await axios.post(`${API}/people/${responsavelId}/dependentes`, payload);
      toast.success(dependente?.id ? "Dependente atualizado" : "Dependente cadastrado");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err, "Erro ao salvar dependente"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="dependent-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Baby className="h-5 w-5 text-sky-600" />{dependente?.id ? "Editar dependente" : "Cadastrar dependente"}</DialogTitle>
          <DialogDescription>Crianças até 11 anos entram automaticamente no Ministério Infantil.</DialogDescription>
        </DialogHeader>
        <form onSubmit={salvar} className="space-y-3">
          <DependentFields form={form} setForm={setForm} responsavelTelefone={responsavelTelefone} />
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="dependent-save">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Salvar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Lista de dependentes de uma pessoa, com cadastrar/editar/remover
export const DependentsList = ({ responsavelId, responsavelTelefone, compact = false, onChanged }) => {
  const [deps, setDeps] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    if (!responsavelId) return;
    axios.get(`${API}/people/${responsavelId}/dependentes`).then(r => setDeps(r.data || [])).catch(() => setDeps([]));
  };
  useEffect(load, [responsavelId]);
  const changed = () => { load(); onChanged?.(); };

  const remover = async (d) => {
    if (!window.confirm(`Remover ${d.nome_completo} dos dependentes?`)) return;
    try {
      await axios.delete(`${API}/people/${responsavelId}/dependentes/${d.id}`);
      toast.success("Dependente removido");
      changed();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao remover"));
    }
  };

  if (!responsavelId) return null;
  return (
    <div className="space-y-2" data-testid="dependents-list">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium flex items-center gap-1.5"><Baby className="h-4 w-4 text-sky-600" /> Dependentes {deps.length > 0 && <Badge variant="secondary" className="text-[10px]">{deps.length}</Badge>}</p>
        <Button type="button" size="sm" variant="outline" className="h-8 gap-1" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-dependent-btn">
          <Plus className="h-3.5 w-3.5" /> {compact ? "Dependente" : "Cadastrar dependente"}
        </Button>
      </div>
      {deps.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nenhum dependente cadastrado.</p>
      ) : deps.map(d => (
        <div key={d.id} className="flex items-center gap-2 p-2 rounded-lg border bg-sky-50/40" data-testid={`dependent-${d.id}`}>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{d.nome_completo} <span className="text-xs text-muted-foreground font-normal">· {d.idade} anos</span></p>
            <p className="text-[11px] text-muted-foreground truncate">
              {d.ministerio_infantil_id ? "Ministério Infantil" : "Fora da faixa infantil"}
              {d.telefone && <> · {d.telefone}{d.herda_telefone !== false ? " (do responsável)" : ""}</>}
              {d.alergias && d.alergias.toLowerCase() !== "nenhuma" && <span className="text-amber-600 inline-flex items-center gap-0.5 ml-1"><AlertTriangle className="h-3 w-3" />{d.alergias}</span>}
            </p>
          </div>
          <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(d); setOpen(true); }} data-testid={`edit-dependent-${d.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => remover(d)} data-testid={`remove-dependent-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
      <DependentFormDialog open={open} onOpenChange={setOpen} responsavelId={responsavelId} responsavelTelefone={responsavelTelefone} dependente={editing} onSaved={changed} />
    </div>
  );
};
